import * as Sentry from '@sentry/react'
import { Analytics } from '@vercel/analytics/react'
import { Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { useUserSettings } from './api/hooks/useSettings'
import { AppViewport } from './components/AppViewport'
import { AuthGuard } from './components/AuthGuard'
import { ErrorBoundary } from './components/ErrorBoundary'
import { GlobalLoading } from './components/GlobalLoading'
import { Layout } from './components/Layout'
import { useExerciseProcessor } from './hooks/useExerciseProcessor'
import { useSectionHydration } from './hooks/useSectionHydration'
import { useThemeInitialization } from './hooks/useThemeInitialization'
import { AddSectionsPage, DashboardPage, SettingsPage } from './pages'
import LoginPage from './pages/auth/LoginPage'
import { EmailConfirmedPage } from './pages/EmailConfirmedPage'
import ProfilePage from './pages/ProfilePage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import SetupPage from './pages/setup/SetupPage'
import SplitBuilderPage from './pages/SplitBuilderPage'
import WorkoutsPage from './pages/WorkoutsPage'
import { useAuth } from './stores/authStore'

Sentry.init({
  dsn: 'https://9e68d15a5357a5883bd83bbed44134d4@o4509649142743041.ingest.de.sentry.io/4509649146413136',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
})

function AppContent() {
  const { user: _user } = useAuth()

  // Initialize theme from localStorage on app start
  useThemeInitialization()

  // Ensure user settings exist for authenticated users
  // This will create settings with defaults if they don't exist
  useUserSettings()

  // Hydrate Zustand store with enabledSections from database
  useSectionHydration()

  // Initialize ExerciseProcessor with user's muscle group overrides
  useExerciseProcessor()

  return (
    <Suspense fallback={<GlobalLoading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes - require authentication */}
        <Route
          path="/setup"
          element={
            <AuthGuard>
              <SetupPage />
            </AuthGuard>
          }
        />
        {/* Main app routes - require authentication */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <Layout />
            </AuthGuard>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="dashboard" element={<Navigate to="/" replace />} />
          <Route path="workouts" element={<WorkoutsPage />} />
                    <Route path="split" element={<SplitBuilderPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="add-sections" element={<AddSectionsPage />} />
        </Route>

        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  )
}

function App() {
  return (
    <>
      <BrowserRouter>
        <ErrorBoundary>
          <AppViewport>
            <AppContent />
            <Toaster />
          </AppViewport>
        </ErrorBoundary>
      </BrowserRouter>
      <Analytics />
    </>
  )
}

export default App
